/* import __COLOCATED_TEMPLATE__ from './editor.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type { TaskGenerator } from 'ember-concurrency';
import type NotificationsService from 'embercom/services/notifications-service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type Router from '@ember/routing/router-service';
import { get, post, put } from 'embercom/lib/ajax';

interface ChannelIdentityRule {
  id?: string;
  channel: string | null;
  channel_display_name?: string;
  audience: string;
  verification_method: string;
  fallback_rule: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    ruleId?: string;
    isFallback?: boolean;
  };
}

const MESSENGER_SECURITY_CHANNELS = ['web', 'ios', 'android'];

export default class ChannelIdentityRulesEditor extends Component<Signature> {
  @service declare router: Router;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked rule: ChannelIdentityRule | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    if (args.ruleId) {
      taskFor(this.loadRule).perform(args.ruleId);
    } else {
      this.rule = {
        channel: 'web',
        audience: 'users_and_leads',
        verification_method: 'no_verification',
        fallback_rule: args.isFallback || false,
      };
    }
  }

  get channels() {
    return [
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.web'),
        value: 'web',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.ios'),
        value: 'ios',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.android'),
        value: 'android',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.email'),
        value: 'email',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.whatsapp'),
        value: 'whatsapp',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.facebook'),
        value: 'facebook',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.instagram'),
        value: 'instagram',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.sms'),
        value: 'sms',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.twitter'),
        value: 'twitter',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.phone-call'),
        value: 'phone_call',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.slack'),
        value: 'slack',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.discord'),
        value: 'discord',
      },
    ];
  }

  get audiences() {
    return [
      {
        text: this.intl.t(
          'new-settings.workspace.channel-identity-rules.audiences.users-and-leads',
        ),
        value: 'users_and_leads',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.audiences.users-only'),
        value: 'users_only',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.audiences.leads-only'),
        value: 'leads_only',
      },
    ];
  }

  get verificationMethods() {
    return [
      {
        text: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.no-verification',
        ),
        value: 'no_verification',
      },
      {
        text: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.email-otp',
        ),
        value: 'email_otp',
      },
      {
        text: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.messenger-security',
        ),
        value: 'messenger_security',
      },
    ];
  }

  @dropTask
  *loadRule(id: string): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response: any = yield get(`/ember/channel_identity_rules/${id}?app_id=${appId}`);
      if (response.channel_identity_rule) {
        this.rule = response.channel_identity_rule;
      }
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.channel-identity-rules.notifications.load-error'),
      );
    }
  }

  get isLoading(): boolean {
    return taskFor(this.loadRule).isRunning;
  }

  get isNew(): boolean {
    return !this.rule?.id;
  }

  get isFallback(): boolean {
    return this.rule?.fallback_rule || false;
  }

  get indexRoute(): string {
    return 'apps.app.settings.workspace.security';
  }

  get indexRouteQuery() {
    return { tab: 'customer-authentication' };
  }

  get availableChannels() {
    return this.channels;
  }

  get selectedChannel(): string | undefined {
    return this.rule?.channel || undefined;
  }

  @action
  setChannel(value: string): void {
    if (this.rule) {
      this.rule = { ...this.rule, channel: value };
    }
  }

  @action
  setAudience(value: string): void {
    if (this.rule) {
      this.rule = { ...this.rule, audience: value };
    }
  }

  @action
  setVerificationMethod(value: string): void {
    if (this.rule) {
      this.rule = { ...this.rule, verification_method: value };
    }
  }

  @dropTask
  *save(): TaskGenerator<void> {
    if (!this.rule) {
      return;
    }

    try {
      let appId = this.appService.app.id;
      let payload = {
        channel: this.rule.fallback_rule ? null : this.rule.channel,
        audience: this.rule.audience,
        verification_method: this.rule.verification_method,
        fallback_rule: this.rule.fallback_rule,
      };

      if (this.isNew) {
        yield post(`/ember/channel_identity_rules?app_id=${appId}`, {
          channel_identity_rule: payload,
        });
        this.notificationsService.notifyConfirmation(
          this.intl.t('new-settings.workspace.channel-identity-rules.notifications.created'),
        );
      } else {
        yield put(`/ember/channel_identity_rules/${this.rule.id}?app_id=${appId}`, {
          channel_identity_rule: payload,
        });
        this.notificationsService.notifyConfirmation(
          this.intl.t('new-settings.workspace.channel-identity-rules.notifications.updated'),
        );
      }

      this.router.transitionTo(this.indexRoute);
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.channel-identity-rules.notifications.create-error'),
      );
    }
  }

  get isMessengerSecuritySelected(): boolean {
    return this.rule?.verification_method === 'messenger_security';
  }

  get isMessengerSecurityChannelValid(): boolean {
    if (!this.isMessengerSecuritySelected) {
      return true;
    }
    if (this.isFallback) {
      return false;
    }
    return MESSENGER_SECURITY_CHANNELS.includes(this.rule?.channel || '');
  }

  get isMessengerSecurityAudienceValid(): boolean {
    if (!this.isMessengerSecuritySelected) {
      return true;
    }
    return this.rule?.audience === 'users_only';
  }

  get messengerSecurityValidationError(): string | null {
    if (!this.isMessengerSecuritySelected) {
      return null;
    }
    if (!this.isMessengerSecurityChannelValid || !this.isMessengerSecurityAudienceValid) {
      return this.intl.t(
        'new-settings.workspace.channel-identity-rules.validation.messenger-security-invalid',
      );
    }
    return null;
  }

  get isSaveDisabled(): boolean {
    if (!this.rule) {
      return true;
    }
    if (this.isNew && !this.isFallback && !this.rule.channel) {
      return true;
    }
    if (!this.isMessengerSecurityChannelValid || !this.isMessengerSecurityAudienceValid) {
      return true;
    }
    return false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::ChannelIdentityRules::Editor': typeof ChannelIdentityRulesEditor;
    'new-settings/workspace/channel-identity-rules/editor': typeof ChannelIdentityRulesEditor;
  }
}
